import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatOptionSelectionChange } from "@angular/material/core";
import { MatTableDataSource } from "@angular/material/table";
import { debounceTime, tap } from "rxjs/operators";
import { Observable } from "rxjs";

export interface GenericMultiSelectItem {
  id: number | string;
  name: string;
  [key: string]: any;
}

@Component({
  selector: "app-generic-multi-select-autocomplete",
  templateUrl: "./generic-multi-select-autocomplete.component.html",
  styleUrls: ["./generic-multi-select-autocomplete.component.scss"],
  standalone: false
})
export class GenericMultiSelectAutocompleteComponent
  implements OnInit, OnChanges
{
  @ViewChild("inputField") inputField: ElementRef<HTMLInputElement>;

  @Input() items: GenericMultiSelectItem[] = [];
  @Input() itemsObservable: Observable<GenericMultiSelectItem[]>;
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() displayProperty: string = "name";
  @Input() valueProperty: string = "id";
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() set selectedItems(value: GenericMultiSelectItem[]) {
    this._selectedItems = value || [];
    this.dataSource.data = this._selectedItems;
    this.updateDisplayValue();
  }
  get selectedItems(): GenericMultiSelectItem[] {
    return this._selectedItems;
  }

  @Output() onSelectionChange = new EventEmitter<GenericMultiSelectItem[]>();

  filteredItems: GenericMultiSelectItem[] = [];
  form = new UntypedFormControl();
  loading = false;
  private _selectedItems: GenericMultiSelectItem[] = [];
  searchTerm = "";
  dataSource: MatTableDataSource<GenericMultiSelectItem> =
    new MatTableDataSource<GenericMultiSelectItem>();
  displayedColumns: string[] = ["name", "actions"];

  ngOnChanges(changes: SimpleChanges) {
    if (changes["disabled"]) {
      if (this.form) {
        if (changes["disabled"].currentValue) {
          this.form.disable();
          this._selectedItems = [];
          this.dataSource.data = [];
          this.form.setValue("");
        } else {
          this.form.enable();
        }
      }
    }
  }

  ngOnInit() {
    if (this.itemsObservable) {
      this.loading = true;
      this.itemsObservable.subscribe(
        (items) => {
          this.items = items;
          this.filteredItems = this.getFilteredItems("");
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
    } else {
      this.filteredItems = this.getFilteredItems("");
    }

    this.form.valueChanges
      .pipe(
        debounceTime(200),
        tap(() => (this.loading = true))
      )
      .subscribe((value) => {
        this.searchTerm = value || "";
        this.filteredItems = this.getFilteredItems(value);
        this.loading = false;
      });

    if (this.disabled) {
      this.form.disable();
    }
  }

  getFilteredItems(searchTerm: string | null): GenericMultiSelectItem[] {
    if (!searchTerm || searchTerm.trim() === "") {
      return this.items;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return this.items.filter((item) => {
      const displayValue =
        item[this.displayProperty]?.toString().toLowerCase() || "";
      return displayValue.includes(searchLower);
    });
  }

  onPanelOpened() {
    this.searchTerm = "";
    this.filteredItems = this.items;
  }

  onInputFocus() {
    if (!this.searchTerm) {
      this.form.setValue("");
      this.filteredItems = this.items;
    }
  }

  getDisplayValue(): string {
    return "";
  }

  isItemSelected(item: GenericMultiSelectItem): boolean {
    return this._selectedItems.some(
      (selected) => selected[this.valueProperty] === item[this.valueProperty]
    );
  }

  toggleSelection(item: GenericMultiSelectItem) {
    const index = this._selectedItems.findIndex(
      (selected) => selected[this.valueProperty] === item[this.valueProperty]
    );

    if (index >= 0) {
      this._selectedItems.splice(index, 1);
    } else {
      this._selectedItems.push(item);
    }

    this.dataSource.data = this._selectedItems;
    this.updateDisplayValue();
    this.onSelectionChange.emit(this._selectedItems);
  }

  remove(item: GenericMultiSelectItem) {
    const index = this._selectedItems.findIndex(
      (selected) => selected[this.valueProperty] === item[this.valueProperty]
    );

    if (index >= 0) {
      this._selectedItems.splice(index, 1);
      this.dataSource.data = this._selectedItems;
      this.onSelectionChange.emit(this._selectedItems);
    }
  }

  updateDisplayValue() {
    const displayValue = this.getDisplayValue();
    if (!this.searchTerm) {
      this.form.setValue(displayValue, { emitEvent: false });
    }
  }

  clearAll() {
    this._selectedItems = [];
    this.dataSource.data = [];
    this.form.setValue("");
    this.onSelectionChange.emit(this._selectedItems);
  }
}
